import {
  buildNavigationGroups,
  type ResolvedNavigationGroup,
} from "@/config/sidebar-config";
import { usePermissions } from "@/hooks/usePermissions";
import { ROLES } from "@medicorepay/shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Counter, counterService } from "../../services/counter.service";
import { storeService, Store as StoreType } from "../../services/store.service";
import { useAuthStore } from "../../stores/authStore";
import { useBillingSessionStore } from "../../stores/billingSessionStore";

const BUSINESS_OWNER_ROLES: string[] = [
  ROLES.SUPER_ADMIN,
  ROLES.BUSINESS_OWNER,
];

export function useDashboardData() {
  const navigate = useNavigate();
  const {
    user,
    logout,
    selectedStoreId,
    setSelectedStore,
    switchStore,
    selectedCounterId,
    setSelectedCounter,
  } = useAuthStore();
  const { hasPermission, hasRole, roleName } = usePermissions();

  const {
    activeSession,
    showOpenSessionModal,
    showCloseSessionModal,
    showSwitchWarningModal,
    pendingSwitch,
    setShowOpenSessionModal,
    setShowCloseSessionModal,
    setShowSwitchWarningModal,
    setPendingSwitch,
    fetchActiveSession,
  } = useBillingSessionStore();

  const [stores, setStores] = useState<StoreType[]>([]);
  const [counters, setCounters] = useState<Counter[]>([]);

  const isBusinessOwner = BUSINESS_OWNER_ROLES.includes(roleName || "");

  const availableStores = useMemo(() => {
    if (isBusinessOwner) return stores;
    return user?.stores || [];
  }, [isBusinessOwner, stores, user?.stores]);

  const navigationGroups: ResolvedNavigationGroup[] = useMemo(() => {
    return buildNavigationGroups(
      user?.sidebarConfig,
      user?.enabledModules || [],
      hasPermission,
      hasRole,
      roleName,
      user?.pageOverrides,
      user?.disabledPageKeys,
    );
  }, [
    user?.sidebarConfig,
    user?.enabledModules,
    user?.pageOverrides,
    user?.disabledPageKeys,
    hasPermission,
    hasRole,
    roleName,
  ]);

  // Load stores for business owners
  useEffect(() => {
    if (isBusinessOwner) {
      storeService
        .getAll()
        .then((data) => setStores(data || []))
        .catch(() => setStores([]));
    }
  }, [isBusinessOwner]);

  // Auto-select first store
  useEffect(() => {
    if (!selectedStoreId && availableStores.length > 0) {
      void setSelectedStore(availableStores[0].id).catch((error) => {
        toast.error(
          error instanceof Error
            ? error.message
            : "Unable to select the default store",
        );
      });
    }
  }, [availableStores, selectedStoreId, setSelectedStore]);

  // Fetch session on store change
  useEffect(() => {
    if (selectedStoreId) {
      fetchActiveSession(selectedStoreId);
    }
  }, [selectedStoreId, fetchActiveSession]);

  // Load counters on store change
  useEffect(() => {
    if (!selectedStoreId) {
      setCounters([]);
      setSelectedCounter(null);
      return;
    }
    counterService
      .getCounters({ page: 1, limit: 100, storeId: selectedStoreId })
      .then((response) => {
        const list = response.data || [];
        setCounters(list);
        if (activeSession?.counterId) {
          setSelectedCounter(activeSession.counterId);
        } else if (!selectedCounterId && list.length > 0) {
          setSelectedCounter(list[0].id);
        } else if (
          selectedCounterId &&
          !list.some((counter) => counter.id === selectedCounterId)
        ) {
          setSelectedCounter(null);
        }
      })
      .catch(() => setCounters([]));
  }, [
    activeSession?.counterId,
    selectedStoreId,
    selectedCounterId,
    setSelectedCounter,
  ]);

  const changeStoreSecurely = useCallback(
    async (newStoreId: string) => {
      try {
        await switchStore(newStoreId);
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "Unable to securely change stores",
        );
      }
    },
    [switchStore],
  );

  const handleStoreChange = useCallback(
    (newStoreId: string) => {
      if (activeSession && newStoreId !== selectedStoreId) {
        setPendingSwitch({ type: "store", id: newStoreId });
        setShowSwitchWarningModal(true);
      } else {
        void changeStoreSecurely(newStoreId);
      }
    },
    [
      activeSession,
      selectedStoreId,
      changeStoreSecurely,
      setPendingSwitch,
      setShowSwitchWarningModal,
    ],
  );

  const handleCounterChange = useCallback(
    (newCounterId: string) => {
      if (activeSession && newCounterId !== activeSession.counterId) {
        setPendingSwitch({ type: "counter", id: newCounterId });
        setShowSwitchWarningModal(true);
      } else {
        setSelectedCounter(newCounterId);
      }
    },
    [
      activeSession,
      setSelectedCounter,
      setPendingSwitch,
      setShowSwitchWarningModal,
    ],
  );

  const handleSessionClosed = useCallback(() => {
    if (pendingSwitch) {
      if (pendingSwitch.type === "store") {
        void changeStoreSecurely(pendingSwitch.id);
      } else {
        setSelectedCounter(pendingSwitch.id);
      }
      setPendingSwitch(null);
    }
    if (selectedStoreId) {
      fetchActiveSession(selectedStoreId);
    }
  }, [
    pendingSwitch,
    changeStoreSecurely,
    setSelectedCounter,
    setPendingSwitch,
    selectedStoreId,
    fetchActiveSession,
  ]);

  const getSwitchTargetName = () => {
    if (!pendingSwitch) return "";
    if (pendingSwitch.type === "store") {
      return availableStores.find((s) => s.id === pendingSwitch.id)?.name || "";
    }
    return counters.find((c) => c.id === pendingSwitch.id)?.name || "";
  };

  const currentStoreName = availableStores.find(
    (s) => s.id === selectedStoreId,
  )?.name;

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out successfully");
    navigate("/login");
  };

  return {
    user,
    hasRole,
    isBusinessOwner,
    availableStores,
    navigationGroups,
    // Store & Counter
    selectedStoreId,
    selectedCounterId,
    stores,
    counters,
    handleStoreChange,
    handleCounterChange,
    // Session
    activeSession,
    showOpenSessionModal,
    showCloseSessionModal,
    showSwitchWarningModal,
    pendingSwitch,
    setShowOpenSessionModal,
    setShowCloseSessionModal,
    setShowSwitchWarningModal,
    setPendingSwitch,
    fetchActiveSession,
    handleSessionClosed,
    getSwitchTargetName,
    currentStoreName,
    // Auth
    handleLogout,
  };
}
